import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { t } from 'i18next';
import { IDonutWithText } from 'PDF/components/Charts/types';
import { IStepsListItem } from 'PDF/components/StepsList/types';
import { getColorStyles, getColorStylesByRecord, getColorType } from 'PDF/helpers/documentStyle';
import { getMagazineTextObject } from 'PDF/helpers/pdfMapping';

import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import { formatNumber, formatNumberToShort } from 'helpers/numbers';

import { colorIndexesFundingNeeds, colorIndexesFundingStrategy } from './chartColors';

export const getMagazineItems = (businessPlanChapters: IBusinessToolForm, businessPlanType: IBusinessToolType, isExamplePage: boolean) => {
  const {
    investmentTotalCost,
    fundingDirectionDetails,
    expectedFundingPeriod,
    loanTerms,
    terms,
    actionsInSearchingForFunding,
    attractingFinancingExperience,
    utilizingSupportPrograms,
  } = businessPlanChapters.RAISING_AND_ALLOCATING_FUNDS;
  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    return [
      [getMagazineTextObject(isExamplePage ? 'Project Cost Analysis. ' : t('pdfPages.mappings.rasingAndAllocating.projectCostAnalysis'), investmentTotalCost)],
      [
        getMagazineTextObject(
          isExamplePage ? 'Funding Direction Details. ' : t('pdfPages.mappings.rasingAndAllocating.fundingDirectionDetails'),
          fundingDirectionDetails,
        ),
        getMagazineTextObject(isExamplePage ? 'Investment Schedule. ' : t('pdfPages.mappings.rasingAndAllocating.investmentSchedule'), expectedFundingPeriod),
      ],
      [getMagazineTextObject(isExamplePage ? 'Investor Terms. ' : t('pdfPages.mappings.rasingAndAllocating.investorTerms'), terms)],
    ];
  }

  return [
    [getMagazineTextObject(isExamplePage ? 'Project Cost Analysis. ' : t('pdfPages.mappings.rasingAndAllocating.projectCostAnalysis'), investmentTotalCost)],
    [
      getMagazineTextObject(
        isExamplePage ? 'Funding Direction Details. ' : t('pdfPages.mappings.rasingAndAllocating.fundingDirectionDetails'),
        fundingDirectionDetails,
      ),
      getMagazineTextObject(isExamplePage ? 'Investment Schedule. ' : t('pdfPages.mappings.rasingAndAllocating.investmentSchedule'), expectedFundingPeriod),
    ],
    [
      getMagazineTextObject(isExamplePage ? 'Loan Conditions. ' : t('pdfPages.mappings.rasingAndAllocating.loanConditions'), loanTerms),
      getMagazineTextObject(isExamplePage ? 'Investor Terms. ' : t('pdfPages.mappings.rasingAndAllocating.investorTerms'), terms),
      getMagazineTextObject(isExamplePage ? 'Funding Search. ' : t('pdfPages.mappings.rasingAndAllocating.fundingSearch'), actionsInSearchingForFunding),
      getMagazineTextObject(
        isExamplePage ? 'Financing Experience. ' : t('pdfPages.mappings.rasingAndAllocating.financingExperience'),
        attractingFinancingExperience,
      ),
      getMagazineTextObject(isExamplePage ? 'Government Support. ' : t('pdfPages.mappings.rasingAndAllocating.governmentSupport'), utilizingSupportPrograms),
    ],
  ];
};

export const getFundingStrategy = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const FUNDING_STRATEGY_SETTINGS: { [key: string]: { label: string; pos: number } } = {
    researchCost: {
      label: isExamplePage ? 'R&D' : t('executiveSummary.financialNeeds.r&d'),
      pos: 1,
    },
    marketingCost: {
      label: isExamplePage ? 'Marketing and Sales' : t('executiveSummary.financialNeeds.marketingCost'),
      pos: 2,
    },
    workingCapitalCost: {
      label: isExamplePage ? 'Working Capital' : t('executiveSummary.financialNeeds.workingCapitalCost'),
      pos: 3,
    },
    equipmentCost: {
      label: isExamplePage ? 'Purchase of Equipment' : t('executiveSummary.financialNeeds.equipmentCost'),
      pos: 4,
    },
    constructionCost: {
      label: isExamplePage ? 'Construction of Buildings and Facilities' : t('executiveSummary.financialNeeds.constructionCost'),
      pos: 5,
    },
    otherCost: {
      label: isExamplePage ? 'Other' : t('executiveSummary.financialNeeds.otherCost'),
      pos: 6,
    },
  };

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);
  const type = getColorType();
  const colors = getColorStyles(colorIndexesFundingStrategy[type]);

  const sortedFinancingNeeds = Object.entries(businessPlanChapters.EXECUTIVE_SUMMARY.financingNeeds).sort(
    (prev, next) => (FUNDING_STRATEGY_SETTINGS[prev[0]]?.pos || 0) - (FUNDING_STRATEGY_SETTINGS[next[0]]?.pos || 0),
  );
  let total = 0;
  const result = sortedFinancingNeeds.reduce<IDonutWithText>(
    (res, [name, { currencyAmount, percentage }]) => {
      if (!percentage) {
        return res;
      }

      total += Number(currencyAmount);
      const physicalValue = formatNumber(Number(currencyAmount));
      +physicalValue !== 0 &&
        res.labels.push(`${FUNDING_STRATEGY_SETTINGS[name]?.label} - ${currencySymbol}${physicalValue}`);
      res.percentages.push(+Number(percentage).toFixed(2));
      res.physicals.push(Number(currencyAmount));

      return res;
    },
    {
      labels: [],
      percentages: [],
      physicals: [],
      title:isExamplePage ? 'Funding Strategy' : t('pdfPages.mappings.rasingAndAllocating.fundingStrategy'),
      colors,
    },
  );

  return {
    ...result,
    total: `${currencySymbol}${formatNumberToShort(total)}`,
  };
};

const convertToPercentages = (sum: number, value: number) => +((value / sum) * 100).toFixed(2);

const calcPercentages = (value1: number, value2: number) => {
  const sum = value1 + value2;

  return [convertToPercentages(sum, value1), convertToPercentages(sum, value2)];
};

const prepareLabel = (label: string, amount: string, currencySymbol: string) => {
  const physicalValue = formatNumber(Number(amount));

  return `${label}${physicalValue !== '0' ? ` - ${currencySymbol}${physicalValue}` : ''}`;
};

export const getProjectCostAndFundingNeeds = (
  businessPlanChapters: IBusinessToolForm,
  isInvestor: boolean,
  isExamplePage: boolean,
): {
  donuts: IDonutWithText[];
  steps: IStepsListItem[];
} => {
  const { fundingAmount, loanBorrowAmount, investmentAmount } = businessPlanChapters.GLOBAL;
  const fundingAmountNum = +fundingAmount;
  const loanBorrowAmountNum = +loanBorrowAmount;
  const investmentAmountNum = +investmentAmount;
  const { financedPortionDescription, fundingDescription, borrowDescription, raisingDescription } =
    businessPlanChapters.RAISING_AND_ALLOCATING_FUNDS;
  const { totalCost: projectTotalCost } = businessPlanChapters.GLOBAL;

  const financedPortionCost = Number(projectTotalCost) - fundingAmountNum;

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);
  const arrayOfColors = getColorStylesByRecord(colorIndexesFundingNeeds);

  const amounts = [
    {
      num: financedPortionCost,
      description: financedPortionDescription,
      name: isExamplePage ? 'Internal funding capacity' : t('pdfPages.mappings.rasingAndAllocating.internalFundCapacity'),
      id: 'internalFundingCapacity',
      color: arrayOfColors[0],
    },
    {
      num: fundingAmountNum,
      description: fundingDescription,
      name: isExamplePage ? 'Funding needs' : t('pdfPages.mappings.rasingAndAllocating.fundingNeeds'),
      id: 'fundingNeeds',
      color: arrayOfColors[1],
    },
    {
      num: investmentAmountNum,
      description: raisingDescription,
      name: isExamplePage ? 'Investment acquisition' : t('pdfPages.mappings.rasingAndAllocating.investAcquisition'),
      id: 'investmentAcquisition',
      color: arrayOfColors[2],
    },
    {
      num: loanBorrowAmountNum,
      description: borrowDescription,
      name: isExamplePage ? 'Loan amount' : t('pdfPages.mappings.rasingAndAllocating.loanAmount'),
      id: 'loanAmount',
      color: arrayOfColors[3],
    },
  ];

  const donuts: IDonutWithText[] = [
    {
      labels: [],
      percentages: [],
      physicals: [],
      title: isExamplePage ? 'Project Cost' : t('pdfPages.mappings.rasingAndAllocating.projCost'),
      colors: [],
    },
    {
      labels: [],
      percentages: [],
      physicals: [],
      title: isExamplePage ? 'Funding Needs' : t('pdfPages.mappings.rasingAndAllocating.fundingNeedsUp'),
      colors: [],
    },
  ];

  const STEP = 2;
  const totals = new Array(STEP).fill(0);

  amounts.forEach(({ id, name, num, color }) => {
    const ifFirstChart = ['internalFundingCapacity', 'fundingNeeds'].includes(id);
    const donutIndex = ifFirstChart ? 0 : 1;

    if (!(isInvestor && id === 'loanAmount') && num > 0) {
      donuts[donutIndex].labels.push(prepareLabel(name, `${num}`, currencySymbol));
      donuts[donutIndex].colors?.push(color);
      donuts[donutIndex].physicals?.push(Number(num));
      totals[donutIndex] += Number(num);

      if (ifFirstChart) {
        donuts[donutIndex].percentages = calcPercentages(amounts[0].num, amounts[1].num);
      } else {
        donuts[donutIndex].percentages = isInvestor ? [100] : calcPercentages(amounts[2].num, amounts[3].num);
      }
    }
  });

  const steps = amounts
    .filter(({ description }) => description !== '')
    .reduce<IStepsListItem[]>((res, { description, name, color }) => {
      res.push({
        trendName: name,
        trendCharacteristics: description,
        color,
      });

      return res;
    }, []);

  return {
    donuts: donuts
      .map((item, index) => ({
        ...item,
        total: `${currencySymbol}${formatNumberToShort(totals[index])}`,
        percentages: item.percentages.filter((percentage) => percentage > 0),
      }))
      .filter(({ percentages }) => percentages.length),
    steps,
  };
};
